<template>
  <div>
    <v-card elevation="0" class="px-7">
      <v-row dense align="center" justify="space-between">
        <v-col cols="12" sm="6">
          <h1 class="fs-16 primary--text font-weight-bold">
            Request Summary
          </h1>
        </v-col>
        <v-col cols="12" sm="auto">
          <v-breadcrumbs class="app-breadcrumbs py-3" :items="breadcrumbs" />
        </v-col>
      </v-row>
    </v-card>
    <div v-if="loading" class="text-center py-12">
      <v-progress-circular size="50" color="primary" indeterminate />
    </div>
    <div v-else-if="isForbidden" class="text-center py-12">
      <h1>OOPS!</h1>
      <p>Sorry, you don't have any permission to access this page</p>
      <v-btn color="primary" to="/sshcp/request" class="btn-medium">
        Back to Request List
      </v-btn>
    </div>
    <v-container class="px-7" fluid v-else>
      <div class="width-85 d-flex justify-end">
        <preval-number
          v-if="detail.afterBRightButton"
          :item="detail"
          :fromSummary="true"
          @fetchData="fetchData"
        />
        <v-btn
          class="btn-medium ml-3"
          depressed
          color="menuActive"
          dark
          @click.prevent="viewDetailFormRequest"
        >
          View Detail Form Request
        </v-btn>
      </div>

      <div class="d-flex justify-center">
        <div class="width-70" v-if="isRejected">
          <v-alert color="red" rounded class="fs-12 py-2 mt-3 mb-0 white--text">
            <v-row align="center" justify="space-between" no-gutters>
              <v-col cols="auto" class="font-weight-bold">
                Your Request Has Been Rejected. Reason:
              </v-col>
              <v-col cols="auto">
                <v-btn @click="showReason = !showReason" color="white" icon>
                  <v-icon>mdi-chevron-{{ showReason ? 'up' : 'down' }}</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-alert>
          <v-slide-y-transition>
            <div v-if="showReason">
              <div class="px-4 py-4 fs-12">
                {{ detail.notes }}
              </div>
              <v-divider />
            </div>
          </v-slide-y-transition>
        </div>
      </div>

      <v-card
        :loading="loading"
        class="elevation-card rounded-12 mb-5 mt-4 ma-auto"
        max-width="70%"
      >
        <v-card-title class="primary text-center ma-auto">
          <v-col class="font-weight-bold white--text text-center w-100 py-0">
            <h2>HCP</h2>
            <p class="fs-16 mb-0">
              <template v-if="hasRevision">
                {{ detail.hcp_code }}
                Revision For {{ detail.unique_code_reference }}
              </template>
              <template v-else>
                {{ detail.hcp_code }}
              </template>
            </p>
          </v-col>
        </v-card-title>
        <v-card-text class="px-5">
          <table width="100%" class="mt-3 mb-3 pl-15 ma-auto">
            <tr>
              <td class="font-weight-bold" width="29.5%">Created Date</td>
              <td>{{ detail.created_date }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold">PO No. / PO Description</td>
              <td>
                {{ detail.po_number }}
                {{ detail.po_desc ? `/ ${detail.po_desc}` : '' }}
              </td>
            </tr>
            <tr>
              <td class="font-weight-bold">Type of Activity</td>
              <td>{{ detail.type_of_activity }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold">Entity / House</td>
              <td>{{ detail.entity }} / {{ detail.house }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold">Region / District</td>
              <td>{{ detail.region }} / {{ detail.district }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold">Total Budget</td>
              <td>Rp{{ detail.total_budget }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold">Total Participant</td>
              <td>{{ detail.total_participant }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold">Cost Per Pax</td>
              <td>Rp{{ detail.cost_per_pax }}</td>
            </tr>
          </table>

          <table
            width="100%"
            class="mt-5 pl-15 ma-auto"
            v-if="detail.preval_number"
          >
            <tr>
              <td width="29.5%" class="font-weight-bold">Preval Number</td>
              <td>{{ detail.preval_number }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold">Vendor Name</td>
              <td>
                {{ detail.vendor_name }}
              </td>
            </tr>
            <tr>
              <td class="font-weight-bold">Vendor Email</td>
              <td>{{ detail.vendor_email }}</td>
            </tr>
          </table>

          <v-simple-table class="mt-5">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="black--text">
                    Activities of Event
                  </th>
                  <th class="black--text">
                    Name of Event
                  </th>
                  <th class="black--text">
                    Date of Event
                  </th>
                  <th class="black--text">
                    Frequency of Event
                  </th>
                  <th class="black--text">
                    Total Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(event, index) in detail.events"
                  :key="'event' + index"
                >
                  <td>{{ event.activities_of_event }}</td>
                  <td>{{ event.name }}</td>
                  <td>{{ event.start_date }} - {{ event.end_date }}</td>
                  <td class="text-center">{{ event.frequency }}</td>
                  <td class="text-right">
                    Rp{{ idr(event.items_total_amount) }}
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <v-col class="py-1">
            <!-- <h3 class="primary--text">Supporting Documents</h3> -->
            <table width="100%" class="mt-3">
              <tr
                v-for="(doc, index) in detail.supporting_docs"
                :key="index + 'docs'"
              >
                <th class="text-left fs-12">{{ doc.title }}</th>
                <td>{{ doc.file_name_origin }}</td>
                <td>
                  <v-btn
                    :loading="doc.downloading"
                    depressed
                    text
                    class="btn-medium primary2--text text-decoration-underline"
                    @click="downloadFile(doc)"
                  >
                    Download
                  </v-btn>
                </td>
              </tr>
            </table>
          </v-col>

          <v-col class="py-1">
            <v-simple-table class="mt-5">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="black--text">
                      #
                    </th>
                    <th class="black--text">
                      Approver
                    </th>
                    <th class="black--text">
                      Approved Date
                    </th>
                    <th class="black--text">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(approval, idxApprove) in detail.approvals"
                    :key="'approval' + idxApprove"
                  >
                    <td>{{ approval.level }}</td>
                    <td>
                      <v-menu
                        open-on-hover
                        top
                        offset-y
                        v-if="approval.user_email.length > 1"
                      >
                        <template v-slot:activator="{ on }">
                          <div v-on="on">
                            {{ approval.user_email.length }} Approvers
                          </div>
                        </template>
                        <v-card
                          flat
                          outlined
                          :elevation="0"
                          style="boder-radius: 25px !important"
                        >
                          <v-card-title
                            class="blue-grey lighten-5 fs-12 px-2 py-0 grey--text text--darken-1"
                          >
                            <span class="font-weight-bold">Approvers</span>
                          </v-card-title>
                          <v-card-text
                            class="fs-12 grey--text text--darken-1 pa-2"
                          >
                            <v-flex
                              xs12
                              v-for="(name, idx) in approval.user_email"
                              :key="'name' + idx"
                            >
                              <v-icon color="primary" class="mr-1 fs-12">
                                {{
                                  name.isApproved
                                    ? 'radio_button_checked'
                                    : 'radio_button_unchecked'
                                }}
                              </v-icon>
                              <span> {{ name.name }} </span>
                            </v-flex>
                          </v-card-text>
                        </v-card>
                      </v-menu>
                      <div v-else-if="approval.user_email.length === 1">
                        {{ approval.user_email[0].name }}
                      </div>
                    </td>
                    <td>{{ approval.approval_date }}</td>
                    <td class="text-capitalize">
                      {{ approval.status }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-col>
          <v-row v-if="detail.approval_button">
            <v-col cols="6">
              <v-btn
                block
                class="btn-medium"
                color="primary"
                @click="notesApproval('approve')"
                :disabled="reasonRejection"
              >
                Approve
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                block
                class="btn-medium"
                color="error"
                @click="notesApproval('reject')"
                :disabled="reasonApproval"
              >
                Reject
              </v-btn>
            </v-col>
          </v-row>
          <v-row
            v-if="(reasonApproval || reasonRejection) && detail.approval_button"
          >
            <v-col cols="12" class="py-0">
              <v-textarea
                :label="
                  `Insert ${
                    reasonApproval ? 'approval' : 'rejection'
                  } notes here`
                "
                single-line
                class="mt-2"
                outlined
                v-model="notes"
                required
              ></v-textarea>
            </v-col>
            <v-col cols="6" class="py-0">
              <v-btn
                block
                class="btn-medium"
                :class="reasonRejection ? 'white--text' : ''"
                color="menuActive"
                :dark="!reasonRejection"
                @click="submitNotesApproval()"
                :disabled="!notes && reasonRejection"
                :loading="loadingApproval"
              >
                Submit
              </v-btn>
            </v-col>
            <v-col cols="6" class="py-0">
              <v-btn
                block
                class="btn-medium"
                color="error"
                @click="
                  reasonApproval = false;
                  reasonRejection = false;
                "
              >
                Cancel
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
      <v-dialog v-model="dialog" max-width="900px">
        <card-progress-tracking
          v-if="progressTracking"
          :loading="loadingDetail"
          :steps="steps"
        />
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { formatDate } from '@/utils/helpers';
import CardProgressTracking from '@/components/SSHCP/CardProgressTracking';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import PrevalNumber from '@/components/SSHCP/PrevalNumber';

export default {
  components: {
    CardProgressTracking,
    PrevalNumber
  },
  props: ['id'],
  data: () => ({
    showReason: false,
    scrollSettings: {
      maxScrollbarLength: 340
    },
    breadcrumbs: [
      {
        text: 'SSHCP',
        to: '/sshcp/'
      },
      {
        text: 'Request Summary',
        to: '/sshcp/request/'
      }
    ],
    loadingDetail: false,
    detail: {},
    loading: false,
    dialog: false,
    reasonApproval: false,
    reasonRejection: false,
    baseUrl: process.env.VUE_APP_BASE_URL,
    notes: '',
    isForbidden: false,
    loadingApproval: false
  }),
  computed: {
    ...mapState('sshcp', {
      progressTracking: 'progressTracking'
    }),
    steps() {
      return this.progressTracking.map(item => {
        let status;
        item.forEach(element => {
          if (element.status !== null) status = element.status;
        });
        let itemApproved = item.find(val => val.approval_date !== null);
        return {
          group_name: item[0].group_name,
          email: item.map(val => val.email.toLowerCase()),
          name: item.map(val => {
            return {
              name: val.user_name,
              isApproved: !!val.approval_date
            };
          }),
          complete: item[0].status === 'approved',
          date: itemApproved ? formatDate(itemApproved.approval_date) : '',
          status: status,
          notes: itemApproved?.notes
        };
      });
    },
    isRejected() {
      return this.detail.notes;
    },
    reason() {
      const reason = this.detail.notes;

      if (!reason) return 'There is no comment available.';

      return reason;
    },
    hasRevision() {
      return this.detail?.unique_code_reference;
    }
  },
  async mounted() {
    if (this.$route.query.approve == '1') {
      this.reasonRejection = false;
      this.reasonApproval = true;
    } else if (this.$route.query.approve == '0') {
      this.reasonRejection = true;
      this.reasonApproval = false;
    }
    this.fetchData();
  },
  methods: {
    viewDetailFormRequest() {
      this.$router.push(`/sshcp/request/${this.id}`);
    },
    notesApproval(type) {
      if (type === 'approve') {
        this.reasonApproval = true;
        this.reasonRejection = false;
      } else {
        this.reasonRejection = true;
        this.reasonApproval = false;
      }
    },
    async fetchData() {
      this.loading = true;
      return this.$store
        .dispatch('sshcp/getSummaryDetail', this.id)
        .then(data => {
          this.loading = false;
          if (data.code === 200) {
            this.isForbidden = false;
            this.detail = data.data.result;
            this.detail.approvals = data.data.result.approvals.map(item => {
              let status;
              item.forEach(element => {
                if (element.status !== null) status = element.status;
              });
              let itemApproved = item.find(val => val.approval_date !== '-');
              return {
                approval_date: itemApproved?.approval_date,
                user_email: item.map(val => {
                  return {
                    name: val.user_email,
                    isApproved: val.approval_date !== '-'
                  };
                }),
                level: item[0].level,
                status
              };
            });
            this.detail.supporting_docs = data.data.result.supporting_docs.map(
              item => {
                return {
                  ...item,
                  downloading: false
                };
              }
            );
          } else if (data.response.data.code === 403) {
            this.isForbidden = true;
            this.loading = false;
          } else {
            this.isForbidden = false;
            this.loading = false;
          }
        })
        .catch(err => {
          console.log('errror', err);
          this.loading = false;
        });
    },
    async getDetail(id) {
      this.dialog = true;
      this.loadingDetail = true;
      await this.$store.dispatch('sshcp/getProgressTracking', id);
      this.loadingDetail = false;
    },
    async downloadFile(doc) {
      doc.downloading = true;
      const res = await this.$store.dispatch('sshcp/downloadSuppDocs', doc.id);
      doc.downloading = false;
      window.open(res.data.url, '_blank');
      // window.open(this.baseUrl + '/v1/sshcp/form-request/download/support-doc/' + id, '_blank');
    },
    submitNotesApproval() {
      let type = this.reasonApproval ? 'approve' : 'reject';
      Swal.fire({
        title: '',
        text: `Are you sure want to ${type} this request?`,
        type: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No'
      }).then(result => {
        if (result.value) {
          if (type === 'approve') {
            this.approve();
          } else {
            this.reject();
          }
        }
      });
    },
    approve() {
      this.loadingApproval = true;
      let params = {
        form_request_id: this.id,
        notes: this.notes
      };
      return this.$store
        .dispatch('sshcp/approveRequest', params)
        .then(data => {
          this.loadingApproval = false;
          if (data.code === 200) {
            this.reasonApproval = false;
            this.reasonRejection = false;
            this.fetchData();
            Swal.fire('Success!', data.message.text_en, 'success');
          } else {
            Swal.fire('Failed!', data.message.text_en, 'error');
            this.loadingApproval = false;
          }
        })
        .catch(err => {
          Swal.fire('Failed!', err.message.text_en, 'error');
          this.loadingApproval = false;
        });
    },
    reject() {
      this.loadingApproval = true;
      let params = {
        form_request_id: this.id,
        notes: this.notes
      };
      return this.$store
        .dispatch('sshcp/rejectRequest', params)
        .then(data => {
          this.loadingApproval = false;
          if (data.code === 200) {
            this.reasonApproval = false;
            this.reasonRejection = false;
            this.fetchData();
            Swal.fire('Success!', data.message.text_en, 'success');
          } else {
            Swal.fire('Failed!', data.message.text_en, 'error');
            this.loadingApproval = false;
          }
        })
        .catch(err => {
          Swal.fire('Failed!', err.message.text_en, 'error');
          this.loadingApproval = false;
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.width-85 {
  width: 85%;
}
.width-70 {
  width: 70%;
}
.hoverable-tr {
  cursor: pointer;

  &:hover {
    background: #dfdfdf;
  }
}

.drawer-menu--scroll {
  max-height: 600px;
  overflow: auto;
}
</style>
